import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Table, Nav, Tab, Spinner, Alert, Modal, Form, Badge } from 'react-bootstrap';
import '../styles/reservationListTabs.css';
import {
  Reservation,
  getMyReservations,
  endUsage,
  cancelReservation,
  submitEndUseRecord
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import EndUseRecordModal from './EndUseRecordModal';
import {
  getEndUseRules,
  getInstrumentFromReservation,
  reservationHasEndUseRecord
} from '../utils/endUseRecord';

const categorizeReservations = (reservations: Reservation[]) => {
  const now = dayjs();
  const categorized = {
    using: [] as Reservation[],
    queued: [] as Reservation[],
    pending: [] as Reservation[],
    history: [] as Reservation[]
  };

  reservations.forEach((reservation) => {
    const startTime = dayjs(reservation.startTime);
    const endTime = dayjs(reservation.endTime);

    if (reservation.status === '使用中') {
      categorized.using.push(reservation);
    } else if (reservation.status === '待审核') {
      categorized.pending.push(reservation);
    } else if (reservation.status === '已批准') {
      if (startTime.isAfter(now)) {
        categorized.queued.push(reservation);
      } else if (endTime.isBefore(now)) {
        categorized.history.push(reservation);
      } else {
        categorized.using.push(reservation);
      }
    } else if (
      reservation.status === '已完成' ||
      reservation.status === '已取消' ||
      reservation.status === '已拒绝'
    ) {
      categorized.history.push(reservation);
    }
  });

  return categorized;
};

const MyInstrumentReservations: React.FC = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('using');
  const [showEndUsageModal, setShowEndUsageModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [usageNotes, setUsageNotes] = useState('');
  const [endUseModalMode, setEndUseModalMode] = useState<'submit' | 'view'>('submit');
  const [showEndUseModal, setShowEndUseModal] = useState(false);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMyReservations();
      if (response.success) {
        setReservations(response.data || []);
      } else {
        setError(response.error || '获取预约数据失败');
      }
    } catch {
      setError('获取预约数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchReservations();
    const interval = setInterval(() => void fetchReservations(), 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchReservations]);

  const handleCancelClick = async (reservationId: string) => {
    try {
      const cancelRes = await cancelReservation(reservationId);
      if (cancelRes.success) {
        await fetchReservations();
        return;
      }
      setError(cancelRes.error || '取消失败');
    } catch {
      setError('操作失败');
    }
  };

  const handleEndUsage = async () => {
    if (!selectedReservation) return;
    try {
      const response = await endUsage(selectedReservation._id!, { usageNotes });
      if (response.success) {
        await fetchReservations();
        setShowEndUsageModal(false);
        setSelectedReservation(null);
        setUsageNotes('');
      } else {
        setError(response.error || '结束使用失败');
      }
    } catch {
      setError('结束使用失败');
    }
  };

  const handleEndUseSubmit = async (payload: Parameters<typeof submitEndUseRecord>[1]) => {
    if (!selectedReservation?._id) return;
    const isActive = selectedReservation.status === '使用中';
    if (isActive) {
      const response = await endUsage(selectedReservation._id, {
        usageNotes: payload.notes,
        endUseRecord: payload
      });
      if (!response.success) {
        throw new Error(response.error || '结束使用失败');
      }
      await fetchReservations();
      setShowEndUseModal(false);
      setSelectedReservation(null);
      return;
    }
    const response = await submitEndUseRecord(selectedReservation._id, payload);
    if (!response.success) {
      throw new Error(response.error || '提交失败');
    }
    await fetchReservations();
  };

  const openEndUsageModal = (reservation: Reservation) => {
    const ins = getInstrumentFromReservation(reservation);
    const { enabled } = getEndUseRules(ins);
    setSelectedReservation(reservation);
    if (enabled) {
      setEndUseModalMode('submit');
      setShowEndUseModal(true);
      return;
    }
    setUsageNotes('');
    setShowEndUsageModal(true);
  };

  const openEndUseRecordModal = (reservation: Reservation, mode: 'submit' | 'view') => {
    setSelectedReservation(reservation);
    setEndUseModalMode(mode);
    setShowEndUseModal(true);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case '使用中':
        return 'success';
      case '已批准':
        return 'primary';
      case '待审核':
        return 'warning';
      case '已拒绝':
        return 'danger';
      case '已完成':
        return 'secondary';
      case '已取消':
        return 'dark';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status: string) => status;

  const instrumentName = (reservation: Reservation) => {
    const ins = reservation.instrumentId;
    if (ins && typeof ins === 'object' && 'name' in ins) {
      return (ins as { name?: string }).name || '—';
    }
    return '—';
  };

  const isBooker = (reservation: Reservation) =>
    user &&
    typeof reservation.userId === 'object' &&
    ((reservation.userId as { _id?: string })._id === user.id ||
      (reservation.userId as { username?: string }).username === user.username);

  const renderEndUseActions = (reservation: Reservation) => {
    const ins = getInstrumentFromReservation(reservation);
    const { enabled } = getEndUseRules(ins);
    if (!enabled || reservation.status !== '已完成') return null;
    const hasRecord = reservationHasEndUseRecord(reservation);
    if (!isBooker(reservation)) return null;
    return (
      <div className="d-flex flex-wrap gap-1">
        {!hasRecord && (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => openEndUseRecordModal(reservation, 'submit')}
          >
            提交结束使用记录
          </Button>
        )}
        {hasRecord && (
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => openEndUseRecordModal(reservation, 'view')}
          >
            查看我的记录
          </Button>
        )}
      </div>
    );
  };

  const renderTable = (list: Reservation[], showEndUseColumn = false) => {
    if (list.length === 0) {
      return <div className="text-center py-4 text-muted">暂无预约记录</div>;
    }

    return (
      <Table responsive striped hover>
        <thead>
          <tr>
            <th>仪器名称</th>
            <th>预约时间</th>
            <th>使用时长</th>
            <th>预约目的</th>
            <th>状态</th>
            <th>审批人</th>
            <th>审批时间</th>
            {showEndUseColumn && <th>结束使用记录</th>}
            {user && <th>使用控制</th>}
          </tr>
        </thead>
        <tbody>
          {list.map((reservation) => (
            <tr key={reservation._id}>
              <td>
                <strong>{instrumentName(reservation)}</strong>
              </td>
              <td>
                <div>{dayjs(reservation.startTime).format('YYYY-MM-DD HH:mm')}</div>
                <div className="text-muted small">至 {dayjs(reservation.endTime).format('HH:mm')}</div>
              </td>
              <td>{dayjs(reservation.endTime).diff(dayjs(reservation.startTime), 'minute')} 分钟</td>
              <td>{reservation.purpose || '无'}</td>
              <td>
                <Badge bg={getStatusVariant(reservation.status)}>{getStatusText(reservation.status)}</Badge>
              </td>
              <td>
                {typeof reservation.approvedBy === 'object' && reservation.approvedBy
                  ? reservation.approvedBy.name || reservation.approvedBy.username || '—'
                  : '—'}
              </td>
              <td>
                {reservation.approvedAt
                  ? dayjs(reservation.approvedAt).format('YYYY-MM-DD HH:mm')
                  : '—'}
              </td>
              {showEndUseColumn && (
                <td>
                  {reservation.status === '已完成' &&
                  getEndUseRules(getInstrumentFromReservation(reservation)).enabled ? (
                    reservationHasEndUseRecord(reservation) ? (
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0"
                        onClick={() => openEndUseRecordModal(reservation, 'view')}
                      >
                        查看记录
                      </Button>
                    ) : (
                      <span className="text-muted small">未提交</span>
                    )
                  ) : (
                    '—'
                  )}
                </td>
              )}
              {user && isBooker(reservation) && (
                <td>
                  {(reservation.status === '已批准' || reservation.status === '待审核') &&
                    new Date(reservation.endTime) > new Date() && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => void handleCancelClick(reservation._id!)}
                      >
                        取消预约
                      </Button>
                    )}
                  {reservation.status === '使用中' && (
                    <Button
                      size="sm"
                      variant="warning"
                      className="ms-1"
                      onClick={() => openEndUsageModal(reservation)}
                    >
                      结束使用
                    </Button>
                  )}
                  {renderEndUseActions(reservation)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const categorized = useMemo(() => categorizeReservations(reservations), [reservations]);
  const selectedEndUseGroups = getEndUseRules(
    selectedReservation ? getInstrumentFromReservation(selectedReservation) : null
  ).photoGroups;

  if (loading && reservations.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <div className="mt-3">加载中...</div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" className="mb-3" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Tab.Container
        id="my-instrument-reservations-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k != null && k !== '' ? String(k) : 'using')}
        transition={false}
      >
        <div className="lims-reservation-tabs-toolbar">
          <Nav variant="tabs" className="lims-reservation-tabs flex-grow-1" role="tablist">
            <Nav.Item>
              <Nav.Link as="button" type="button" eventKey="using" role="tab">
                使用中 ({categorized.using.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" eventKey="queued" role="tab">
                排队中 ({categorized.queued.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" eventKey="pending" role="tab">
                待审批 ({categorized.pending.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" eventKey="history" role="tab">
                历史申请 ({categorized.history.length})
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Button
            type="button"
            size="sm"
            variant="primary"
            className="lims-reservation-refresh-btn flex-shrink-0"
            onClick={() => void fetchReservations()}
          >
            刷新
          </Button>
        </div>

        <Tab.Content>
          <Tab.Pane eventKey="using">
            <Card>
              <Card.Header>
                <h6 className="mb-0">当前使用中的预约</h6>
                <small className="text-muted">
                  含状态为「使用中」或时段内仍为「已批准」的预约（系统将自动开始）
                </small>
              </Card.Header>
              <Card.Body>{renderTable(categorized.using)}</Card.Body>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="queued">
            <Card>
              <Card.Header>
                <h6 className="mb-0">已批准但未到时间的预约</h6>
                <small className="text-muted">到达预约时间后系统将自动进入使用中</small>
              </Card.Header>
              <Card.Body>{renderTable(categorized.queued)}</Card.Body>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="pending">
            <Card>
              <Card.Header>
                <h6 className="mb-0">等待审批的预约</h6>
              </Card.Header>
              <Card.Body>{renderTable(categorized.pending)}</Card.Body>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="history">
            <Card>
              <Card.Header>
                <h6 className="mb-0">历史预约记录</h6>
                <small className="text-muted">可在此提交或查看结束使用状态记录（照片+说明）</small>
              </Card.Header>
              <Card.Body>{renderTable(categorized.history, true)}</Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <Modal show={showEndUsageModal} onHide={() => setShowEndUsageModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>结束使用仪器</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReservation && (
            <div>
              <div className="mb-3">
                <strong>仪器：</strong>
                {instrumentName(selectedReservation)}
              </div>
              <div className="mb-3">
                <strong>预约时间：</strong>
                {dayjs(selectedReservation.startTime).format('YYYY-MM-DD HH:mm')} -{' '}
                {dayjs(selectedReservation.endTime).format('HH:mm')}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>使用备注</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={usageNotes}
                  onChange={(e) => setUsageNotes(e.target.value)}
                  placeholder="请输入使用过程中的备注信息..."
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEndUsageModal(false)}>
            取消
          </Button>
          <Button variant="warning" onClick={() => void handleEndUsage()}>
            确认结束使用
          </Button>
        </Modal.Footer>
      </Modal>

      <EndUseRecordModal
        show={showEndUseModal}
        mode={endUseModalMode}
        reservation={selectedReservation}
        photoGroups={selectedEndUseGroups}
        onHide={() => {
          setShowEndUseModal(false);
          setSelectedReservation(null);
        }}
        onSubmit={async (payload) => {
          try {
            await handleEndUseSubmit(payload);
          } catch (e) {
            setError(e instanceof Error ? e.message : '提交失败');
            throw e;
          }
        }}
      />
    </div>
  );
};

export default MyInstrumentReservations;
